import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { useSession } from '../lib/session';
import { Alert, Button, Card, Spinner } from '../components/ui';
import { localizeMessage, useLocale, useT } from '../i18n/locale';

interface VerifyResult {
  trialStarted: boolean;
  email: string;
  message: string;
  messageCode?: string;
}

/**
 * Two jobs, told apart by the presence of a token:
 *
 *  - with ?token=  : report the outcome of the link. Nothing more — no
 *    session is created, because a link sitting in an inbox proves an
 *    address, not an identity. The user signs in as normal afterwards.
 *  - without       : tell a signed-in but unverified user what to do next.
 */
export default function VerifyEmailScreen() {
  const t = useT();
  const { locale } = useLocale();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, refresh } = useSession();

  const [state, setState] = useState<'idle' | 'working' | 'done' | 'error'>(
    token ? 'working' : 'idle',
  );
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  const busy = state === 'working';

  useEffect(() => {
    if (!token) return;

    /*
     * Take the token out of the address bar as soon as it has been read.
     *
     * It is single-use, but until this it stayed in the URL — and so in
     * browser history, in the next page's Referer header, and on screen on a
     * shared machine. replaceState rather than navigate, so this does not add
     * a history entry of its own or re-run the effect.
     */
    window.history.replaceState(null, '', window.location.pathname);

    api
      .post<VerifyResult>('/verify-email', { token })
      .then(async (res) => {
        setResult(res);
        setMessage(localizeMessage(locale, res.messageCode, res.message));
        setState('done');
        // Only to unlock an existing session that was waiting on this; it
        // never creates one.
        if (user) await refresh();
      })
      .catch((e) => {
        setMessage(e instanceof ApiError ? e.message : t('verify.failed'));
        setState('error');
      });
    // Runs once for the token in the URL.
     
  }, [token]);

  async function resend() {
    setState('working');
    setDevLink(null);
    try {
      const res = await api.post<{
        message: string;
        messageCode?: string;
        verificationUrl?: string;
      }>('/resend-verification');
      setMessage(localizeMessage(locale, res.messageCode, res.message));
      setDevLink(res.verificationUrl ?? null);
      setState('idle');
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : t('verify.resendFailed'));
      setState('error');
    }
  }

  // Without a token this page only means something for a signed-in,
  // unverified user.
  if (!token && !user) return <Navigate to="/login" replace />;
  if (!token && user?.emailVerified) return <Navigate to="/" replace />;

  // ---- Outcome of a verification link ------------------------------------
  if (token) {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <Card className="p-7">
          {busy ? (
            <Spinner label={t('verify.verifying')} />
          ) : state === 'done' ? (
            <>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-2xl text-emerald-600">
                ✓
              </div>
              <h2 className="m-0 text-lg font-bold text-slate-800">{t('verify.verified')}</h2>
              <p className="mt-2 mb-0 text-sm text-slate-600">
                <b className="text-slate-900">{result?.email}</b> {t('verify.successSuffix')}
                {result?.trialStarted ? t('verify.trialStartedAppend') : ''}
              </p>
              <div className="mt-5">
                {/* A link, not a redirect: verifying does not sign anyone in. */}
                <Link to="/login" className="no-underline">
                  <Button>{t('verify.toAccount')}</Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-2xl text-red-600">
                ✕
              </div>
              <h2 className="m-0 text-lg font-bold text-slate-800">{t('verify.failedTitle')}</h2>
              <p className="mt-2 mb-0 text-sm text-slate-600">{message}</p>
              <p className="mt-2 mb-0 text-sm text-slate-500">{t('verify.linkOnceNote')}</p>
              <div className="mt-5">
                <Link to="/login" className="no-underline">
                  <Button variant="secondary">{t('verify.toLogin')}</Button>
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    );
  }

  // ---- Signed in but not yet verified ------------------------------------
  return (
    <div className="mx-auto w-full max-w-xl p-6">
      <Card className="p-7">
        <h2 className="m-0 text-lg font-bold text-slate-800">{t('verify.title')}</h2>

        <div className="mt-4 space-y-4">
          {state === 'error' && message ? <Alert tone="error">{message}</Alert> : null}
          {state === 'idle' && message ? <Alert tone="success">{message}</Alert> : null}

          <p className="m-0 text-sm text-slate-600">
            {t('verify.sentTo', { email: user?.email ?? '-' })}
          </p>
          <p className="m-0 text-sm text-slate-500">
            {t('verify.trialNote')}
          </p>

          {devLink ? (
            <Alert tone="warn">
              <span className="block font-bold">{t('verify.devMode')}</span>
              {t('verify.devNote')}
              <a href={devLink} className="mt-2 block break-all font-bold underline">
                {devLink}
              </a>
            </Alert>
          ) : null}

          <Button variant="secondary" onClick={() => void resend()} disabled={busy}>
            {busy ? t('verify.sending') : t('verify.resend')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
